package pages

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import shared.content.ReaptiveHomeContent
import shared.models.ProductCard

@Composable
fun HomePage() {
    // Hero section
    Section(attrs = {
        style {
            property("padding", "6rem 1.5rem 4rem")
            textAlign("center")
            property("background-color", "var(--background)")
        }
    }) {
        Div(attrs = {
            style {
                maxWidth(48.cssRem)
                property("margin", "0 auto")
            }
        }) {
            H1(attrs = {
                style {
                    fontSize(2.75.cssRem)
                    fontWeight(800)
                    property("color", "var(--on-surface)")
                    property("line-height", "1.1")
                    marginBottom(1.5.cssRem)
                }
            }) {
                val parts = ReaptiveHomeContent.heroHeading.split("connect")
                Text(parts[0])
                Span(attrs = {
                    style { property("color", "var(--primary)") }
                }) { Text("connect") }
                Text(parts[1])
            }
            P(attrs = {
                style {
                    fontSize(1.125.cssRem)
                    property("color", "var(--on-surface-variant)")
                    property("line-height", "1.8")
                    maxWidth(36.cssRem)
                    property("margin", "0 auto")
                }
            }) {
                Text(ReaptiveHomeContent.heroSubtitle)
            }
        }
    }

    // Products section
    Section(attrs = {
        style {
            property("padding", "3rem 1.5rem 5rem")
            property("background-color", "var(--surface)")
        }
    }) {
        Div(attrs = {
            style {
                maxWidth(56.cssRem)
                property("margin", "0 auto")
            }
        }) {
            H2(attrs = {
                style {
                    fontSize(1.5.cssRem)
                    fontWeight(700)
                    property("color", "var(--on-surface)")
                    textAlign("center")
                    marginBottom(0.5.cssRem)
                }
            }) { Text(ReaptiveHomeContent.productsHeading) }
            P(attrs = {
                style {
                    fontSize(1.cssRem)
                    property("color", "var(--on-surface-variant)")
                    textAlign("center")
                    marginBottom(2.5.cssRem)
                }
            }) { Text(ReaptiveHomeContent.productsSubtitle) }

            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr")
                    gap(1.5.cssRem)
                }
            }) {
                ProductCardComposable(ReaptiveHomeContent.astroNavya)
                ProductCardComposable(ReaptiveHomeContent.peekopals)
            }
        }
    }

    // Contact section
    Section(attrs = {
        style {
            property("padding", "4rem 1.5rem")
            property("background-color", "var(--background)")
        }
    }) {
        Div(attrs = {
            style {
                maxWidth(36.cssRem)
                property("margin", "0 auto")
                textAlign("center")
            }
        }) {
            H2(attrs = {
                style {
                    fontSize(1.5.cssRem)
                    fontWeight(700)
                    property("color", "var(--on-surface)")
                    marginBottom(0.75.cssRem)
                }
            }) { Text(ReaptiveHomeContent.contactHeading) }
            P(attrs = {
                style {
                    fontSize(1.cssRem)
                    property("color", "var(--on-surface-variant)")
                    marginBottom(1.5.cssRem)
                }
            }) { Text(ReaptiveHomeContent.contactSubtitle) }
            A(attrs = {
                attr("href", "mailto:hello@reaptive.in")
                style {
                    display(DisplayStyle.InlineBlock)
                    property("padding", "0.75rem 2rem")
                    property("background-color", "var(--primary)")
                    property("color", "var(--on-primary)")
                    borderRadius(0.5.cssRem)
                    fontWeight(600)
                    fontSize(1.cssRem)
                    textDecoration("none")
                    property("transition", "opacity 0.2s")
                }
            }) { Text("hello@reaptive.in") }
        }
    }
}

@Composable
private fun ProductCardComposable(product: ProductCard) {
    A(attrs = {
        attr("href", product.url)
        attr("target", "_blank")
        attr("rel", "noopener noreferrer")
        style {
            display(DisplayStyle.Block)
            textDecoration("none")
            borderRadius(1.cssRem)
            padding(2.cssRem)
            property("background-color", "var(--surface-container)")
            property("border", "1px solid var(--outline-variant)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
            property("color", "inherit")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(0.75.cssRem)
            }
        }) {
            Div {
                H3(attrs = {
                    style {
                        fontSize(1.5.cssRem)
                        fontWeight(700)
                        property("color", "var(--primary)")
                        marginBottom(0.25.cssRem)
                    }
                }) { Text(product.name) }
                P(attrs = {
                    style {
                        fontSize(0.8.cssRem)
                        fontWeight(600)
                        property("color", "var(--on-surface-variant)")
                        property("text-transform", "uppercase")
                        letterSpacing(0.05.em)
                    }
                }) { Text(product.tagline) }
            }
            Span(attrs = {
                style {
                    fontSize(1.5.cssRem)
                    property("color", "var(--on-surface-variant)")
                    opacity(0.5)
                }
            }) { Text("\u2192") }
        }
        P(attrs = {
            style {
                fontSize(1.cssRem)
                property("color", "var(--on-surface)")
                property("line-height", "1.7")
            }
        }) { Text(product.description) }
        P(attrs = {
            style {
                marginTop(1.cssRem)
                fontSize(0.875.cssRem)
                property("color", "var(--primary)")
                fontWeight(600)
            }
        }) { Text(product.url.removePrefix("https://").removePrefix("www.")) }
    }
}
